/**
 * One task.
 *
 * This screen existed nowhere before: clicking a task attached to a matter
 * navigated to the matter, so the task itself could never be opened, and a task
 * attached to nothing could not be opened at all. It looked like a bug in the
 * task list, and it was a missing screen.
 *
 * Status and assignee are their own actions rather than fields on a form, and
 * for the same reason throughout this product: handing work to somebody
 * notifies them, and completing something is a statement about the world rather
 * than an edit to a record.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type FirmMember,
  type TaskListItem,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Select,
  Modal,
  Skeleton,
  StatusPill,
  statusTone,
  useToast,
} from '@legalcrm/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { Can } from '@/auth/permissions';
import { formatDateTime, formatRelative, humanise, isPast } from '@/lib/format';
import { PageHeader } from '@/shell/PageHeader';

const CLOSED = new Set(['completed', 'cancelled']);

export function TaskDetailPage() {
  const { taskId = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { me } = useAuth();
  const [assignee, setAssignee] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const query = useApiQuery<TaskListItem & Record<string, unknown>>(
    qk.detail('tasks', taskId),
    STAFF.task(taskId),
    { enabled: Boolean(taskId) },
  );

  const members = useApiQuery<{ results: FirmMember[] } | FirmMember[]>(
    qk.list('users'),
    STAFF.users,
  );
  const memberRows = Array.isArray(members.data)
    ? members.data
    : (members.data?.results ?? []);

  const invalidates = [qk.detail('tasks', taskId), qk.all('tasks'), qk.dashboard()];

  const setStatus = useApiMutation<unknown, string>({
    mutationFn: (status) => api.post(`${STAFF.task(taskId)}/status/`, { status }),
    invalidates,
  });

  const reassign = useApiMutation<unknown, string>({
    mutationFn: (userId) => api.post(`${STAFF.task(taskId)}/assign/`, { assignee: userId }),
    invalidates,
  });

  // Declared above the early returns below. A hook that sits under a conditional
  // `return` changes the hook order between renders and crashes the page on the
  // render where the data arrives -- which typecheck cannot see.
  const remove = useApiMutation<unknown, void>(
    {
      mutationFn: () => api.delete(STAFF.task(taskId)),
      invalidates,
    },
    {
      onSuccess: () => {
        toast({ title: 'Task deleted', tone: 'success' });
        navigate('/tasks');
      },
      onError: () => {
        toast({
          title: 'The task could not be deleted',
          description: 'Only the person who raised a task can delete it.',
          tone: 'warning',
        });
        setConfirmingDelete(false);
      },
    },
  );

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.error || !query.data) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const task = query.data;
  // The creator, not the assignee. Being handed a job is not authority to erase it.
  const mine = Boolean(
    me?.user.id &&
      task &&
      ((task.creator as { id?: string } | null)?.id ?? task.creator) === me.user.id,
  );
  const status = String(task.status ?? '');
  const overdue = !CLOSED.has(status) && isPast(task.due_at as string | null);
  const subjectId = task.subject_id as string | undefined;

  return (
    <>
      <PageHeader
        title={task.title}
        description={
          task.subject_type
            ? `Attached to a ${humanise(String(task.subject_type)).toLowerCase()}`
            : 'Not attached to a matter'
        }
        actions={
          <div className="flex gap-1.5">
            {/*
              Shown only to the person who raised it. Everybody holds
              `task.delete.own`, so the capability alone would draw this button on
              every task including ones handed to you -- and it would fail on click.
              A control that cannot act should not be rendered.
            */}
            {mine && (
              <Can module="task" action="delete">
                <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
                  Delete
                </Button>
              </Can>
            )}
            {!CLOSED.has(status) && (
              <Can module="task" action="edit">
                <Button
                  variant="primary"
                  loading={setStatus.isPending}
                  onClick={() => setStatus.mutate('completed')}
                >
                  Mark complete
                </Button>
              </Can>
            )}
          </div>
        }
      />

      <Modal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        title={`Delete "${task.title}"?`}
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-muted">
            It disappears from task lists and from the matter. The record that it existed
            and that you removed it stays on the file.
          </p>
          <p className="text-sm text-ink-muted">
            If the work was real but is no longer needed, cancel it instead &mdash; that
            leaves it visible with a reason.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmingDelete(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete the task
            </Button>
          </div>
        </div>
      </Modal>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <Card>
            <CardHeader title="Notes" />
            <CardBody>
              <p className="whitespace-pre-wrap text-ink">
                {(task.description as string) || 'Nothing recorded.'}
              </p>
            </CardBody>
          </Card>

          {task.subject_type === 'case' && subjectId && (
            <Card>
              <CardHeader title="Matter" />
              <CardBody>
                <Link
                  to={`/cases/${subjectId}`}
                  className="text-primary hover:underline"
                >
                  Open the matter this belongs to
                </Link>
              </CardBody>
            </Card>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Status" />
            <CardBody className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <StatusPill label={humanise(status)} tone={statusTone(status)} />
                <Badge tone="neutral" size="sm">
                  {humanise(String(task.priority ?? 'normal'))}
                </Badge>
              </div>

              {task.due_at ? (
                <div>
                  <p className={overdue ? 'text-danger' : 'text-ink'}>
                    {formatRelative(task.due_at as string)}
                  </p>
                  {/* The absolute date always sits beside the relative one: "in two
                      days" is not a date anybody can diarise. */}
                  <p className="text-xs text-ink-subtle">
                    {formatDateTime(task.due_at as string)}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-ink-muted">No date set.</p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Assigned to" />
            <CardBody className="flex flex-col gap-3">
              <p className="text-ink">
                {(task.assignee_name as string) || 'Nobody yet'}
              </p>

              <Can module="task" action="edit">
                <div className="flex flex-col gap-2">
                  <Select
                    label="Hand it to"
                    value={assignee}
                    onChange={(event) => setAssignee(event.target.value)}
                    options={[
                      { value: '', label: 'Choose somebody' },
                      ...memberRows
                        .filter((row) => row.status === 'active')
                        .map((row) => ({ value: row.user, label: row.full_name })),
                    ]}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!assignee}
                    loading={reassign.isPending}
                    onClick={() => reassign.mutate(assignee)}
                  >
                    Reassign
                  </Button>
                </div>
              </Can>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}
